'use client';

import React, { useEffect, useRef, useState } from 'react';
import PostHeader from '@/components/(blog)/post/layout/PostHeader';
import Image from 'next/image';
import MainContent from '@/components/(blog)/MainContent';
import PostAside from '@/components/(blog)/post/PostAside';
import VideoComponent from '@/components/VideoPlayer';
import PostFAQ from '@/components/(blog)/post/PostFAQ';
import PostCommentForm from '@/components/(blog)/post/PostCommentForm';

const PageTemplate = ({ page }) => {
    // Always initialize hooks unconditionally
    const [isReviewsVisible, setIsReviewsVisible] = useState(false);
    const reviewsRef = useRef(null);

    // Function to scroll to reviews
    const scrollToReviews = () => {
        const reviews = reviewsRef.current;

        if (reviews) {
            const topPosition = reviews.offsetTop;
            window.scrollTo({ top: topPosition - 200, behavior: 'smooth' });
        }
    };

    // useEffect to observe intersection and set visibility
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsReviewsVisible(true);
                        observer.disconnect(); // Stop observing once it's visible
                    }
                });
            },
            { threshold: 0.1 },
        );

        const currentReviewsRef = reviewsRef.current;

        if (currentReviewsRef) {
            observer.observe(currentReviewsRef);
        }

        return () => {
            if (observer && currentReviewsRef) {
                observer.unobserve(currentReviewsRef);
            }
        };
    }, []);

    // Destructure page fields
    const {
        title,
        databaseId,
        content,
        slug,
        date,
        author,
        featuredImage,
        commentCount,
        optionsForPages,
        posts_acf,
    } = page || {}; // Add fallback to avoid destructuring undefined

    return (
        <div className='lg:w-8/12 container'>
            <div className={'my-8'}>
                <h1 className={'text-4xl leading-[3.5rem] font-bold'}>{title}</h1>
                {featuredImage && optionsForPages?.featuredImage && (
                    <Image
                        src={featuredImage?.node?.sourceUrl}
                        title={featuredImage?.node?.title}
                        alt={featuredImage?.node?.title}
                        width={1200}
                        header={650}
                        className='aspect-video w-full !rounded-3xl my-4'
                        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                    />
                )}
                {optionsForPages?.infoBar && optionsForPages?.comments && (
                    <header>
                        <PostHeader
                            title={title}
                            slug={slug}
                            date={date}
                            author={author}
                            commentCount={commentCount || 0}
                            views={''}
                            scrollToReviews={scrollToReviews}
                            scrollToAuthor={''}
                            avgRate={''}
                            postRatings={''}
                            audioFile={''}
                        />
                    </header>
                )}
            </div>
            <div
                className={`flex flex-col justify-between ${optionsForPages?.sidebar && 'xl:flex-row xl:gap-20'}`}
            >
                <div className={`flex flex-col ${optionsForPages?.sidebar && 'xl:w-8/12'}`}>
                    {/* Page Content */}
                    <MainContent processedContent={content} slug={slug} />
                    {posts_acf?.aparatHnembed && <VideoComponent post={page} />}
                    {optionsForPages?.faq && <PostFAQ faqList={posts_acf?.faqList} />}
                    {optionsForPages?.comments && (
                        <>
                            <span ref={reviewsRef}></span>
                            <div className={'relative mb-3'}>
                                <span className={'text-xl customBorder'}>
                                    نظر شما درباره این مقاله چیست؟
                                </span>
                            </div>
                            <PostCommentForm
                                postId={databaseId}
                                commentCount={commentCount}
                                isReviewsVisible={isReviewsVisible}
                            />
                        </>
                    )}
                </div>
                {optionsForPages?.sidebar && (
                    <PostAside post={page} relatedPosts={page?.relatedPosts} />
                )}
            </div>
        </div>
    );
};

export default PageTemplate;
